import { z } from 'zod'

export enum RequestType {
  /**
   * For trusted requests inside the vpc, not initiated by client
   */
  Internal = '/internal',
  /**
   * For public requests initiated by client, guarded by auth token
   * Data may be stale, since this only reads from S3 and not from syncd.
   * This is the default request type, and it is subject to caching.
   */
  Default = '/data',
  /**
   * For requests about ongoing activity, initiated by client, guarded by auth token
   * Data is fresh, since this reads from syncd, but it will error if
   * the data is no longer live.
   * Not subject to caching.
   */
  Live = '/live'
}

export interface OrgScopedPathParams {
  orgCode: string
  requestType: RequestType
}

export interface StreamScopedPathParams extends OrgScopedPathParams {
  streamId: string
}

export interface ApplicationScopedPathParams extends StreamScopedPathParams {
  applicationId: string
}

export interface LayerScopedPathParams extends ApplicationScopedPathParams {
  layerId: string
}

export interface PaginationParams {
  next?: number
  limit?: number
}

export interface RecordParams {
  record: number
}

/**
 * A sentinel value to request the latest data, rather than a specific record.
 * It can be used in place of a record number in any request to stitch that is
 * scoped to a record.
 *
 * The underlying value is undefined but it is typed as never to prevent the caller
 * from using the literal `undefined` in an ambiguous and potentially incorrect way.
 * Forcing the caller to use the `LATEST_DATA` constant makes the intent absolutely clear.
 *
 * `undefined` works because Stitch defaults to the latest record if none is specified.
 */
export const LATEST_DATA = undefined as never

// Zod schemas for path parameters (used in migration)
export const orgScopedPathSchema = z.object({
  orgCode: z.string(),
  requestType: z.nativeEnum(RequestType)
})

export const streamScopedPathSchema = orgScopedPathSchema.extend({
  streamId: z.string()
})

export const applicationScopedPathSchema = streamScopedPathSchema.extend({
  applicationId: z.string()
})

export const layerScopedPathSchema = applicationScopedPathSchema.extend({
  layerId: z.string()
})

export const paginationQuerySchema = z.object({
  next: z.number().optional(),
  limit: z.number().optional()
})

export const recordQuerySchema = z.object({
  record: z.number().optional()
})
